// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Divider, Form, Grid, Input, Modal, Select, Space, Tabs, Tag, Tooltip, Typography } from "antd";
import { KeyBackupPanel } from "./KeyBackupPanel";
import { NotificationsPanel } from "./NotificationsPanel";
import { DevicesPanel } from "./DevicesPanel";
import { KeyLockPanel } from "./KeyLockPanel";
import { WebAuthnPanel } from "./WebAuthnPanel";
import { BioLockPanel } from "./BioLockPanel";
import { InvitePanel } from "./InvitePanel";
import { PasskeyPanel } from "./PasskeyPanel";
import { witnessedEpochs, witnessAlarm } from "../transparency";
import { AdminConsole } from "./AdminConsole";
import { RecoveryCodesPanel } from "./RecoveryCodesPanel";
import { MediaCachePanel } from "./MediaCachePanel";
import { AppstoreOutlined, CopyOutlined, NumberOutlined, SafetyCertificateTwoTone } from "@ant-design/icons";
import { Homeserver } from "../api";
import { APP_VERSION } from "../version";
import { isNativeApp } from "../server";
import { openPriv } from "../keystore";
import type { CryptoEngine } from "../crypto";
import { friendlyRoomName, type DirectoryRoom, type IdentityProof, type Session, type SigchainLink } from "../types";

const hs = new Homeserver();

// loadDeviceKey reads this device's persisted OpenPGP identity (saved by
// ensureDeviceKey on login) so proofs can be signed by it.
async function loadDeviceKey(deviceID: string): Promise<{ priv: string; pub: string; fingerprint: string } | null> {
  const raw = localStorage.getItem(`zalmoxis.key.${deviceID}`);
  if (!raw) return null;
  try {
    const slot = JSON.parse(raw);
    return { priv: await openPriv(slot.priv), pub: slot.pub, fingerprint: slot.fingerprint }; // unseal the at-rest key
  } catch {
    return null;
  }
}

// Profile is the account panel: identity, change-password, identity proofs, and
// the channels the user has joined (click to open).
export function Profile({
  open,
  session,
  engine,
  engineReady,
  onClose,
  onOpenRoom,
  onDiscussReport,
  inline = false,
}: {
  readonly open: boolean;
  readonly session: Session;
  readonly engine: CryptoEngine | null;
  readonly engineReady: boolean;
  readonly onClose: () => void;
  readonly onOpenRoom: (roomID: string) => void;
  // onDiscussReport (admin) opens a moderator↔reporter room for a report.
  readonly onDiscussReport?: (reportID: string, reporter: string, room?: string) => void;
  // inline renders the body without the Modal wrapper (a mobile bottom-nav screen).
  readonly inline?: boolean;
}) {
  const { message } = App.useApp();
  const screens = Grid.useBreakpoint(); // left tabs on desktop, top tabs on mobile
  const [joined, setJoined] = useState<DirectoryRoom[]>([]);
  const [loading, setLoading] = useState(false);
  const [proofs, setProofs] = useState<IdentityProof[]>([]);
  const [adding, setAdding] = useState(false);
  const [published, setPublished] = useState<Record<string, "ok" | "missing" | "checking">>({});
  const [ktBusy, setKtBusy] = useState(false);
  const [ktResult, setKtResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [canInvite, setCanInvite] = useState(false); // server is invite-mode AND I may mint
  const [webauthnOn, setWebauthnOn] = useState(false); // server offers passkey login
  const [isAdmin, setIsAdmin] = useState(false); // server admin (shows the moderation console)
  const [oidcProviders, setOidcProviders] = useState<{ id: string; name: string }[]>([]); // linkable sign-in providers
  const [form] = Form.useForm();
  const [proofForm] = Form.useForm();

  const proofKey = (p: IdentityProof) => `${p.service}-${p.identity}-${p.seqno}`;

  // checkPublished fetches the proof URL (server-side, CORS-proxied) and confirms
  // the external account actually published this proof's signature — the reverse
  // half of the binding (forward half = the signature itself, verified on load).
  async function checkPublished(p: IdentityProof) {
    if (!p.proof_url || !p.signature) return;
    const key = proofKey(p);
    setPublished((prev) => ({ ...prev, [key]: "checking" }));
    try {
      const { body } = await hs.fetchProof(session.access_token, p.proof_url);
      const norm = (s: string) => s.replace(/\s+/g, "");
      const found = norm(body).includes(norm(p.signature));
      setPublished((prev) => ({ ...prev, [key]: found ? "ok" : "missing" }));
    } catch {
      setPublished((prev) => ({ ...prev, [key]: "missing" }));
    }
  }

  async function loadProofs() {
    if (!engineReady || !engine) return;
    try {
      const chain = await hs.getSigchain(session.access_token, session.user_id);
      if (!chain.anchor_key) {
        setProofs([]);
        return;
      }
      const res = await engine.validateSigchain(session.user_id, chain.anchor_key, chain.links ?? []);
      setProofs(res.proofs ?? []);
    } catch {
      setProofs([]); // no sigchain yet
    }
  }

  useEffect(() => {
    if (!open && !inline) return;
    hs.joinedRooms(session.access_token).then(setJoined).catch(() => undefined);
    void loadProofs();
    // Show the Invites tab only when the server is in invite mode AND I'm allowed
    // to mint (admin always; regular members when member-driven invites are on).
    void (async () => {
      try {
        const [rc, who] = await Promise.all([hs.registrationConfig(), hs.whoami(session.access_token)]);
        setCanInvite(rc.mode === "invite" && (who.admin === true || rc.member_invites));
        setWebauthnOn(rc.webauthn === true);
        setIsAdmin(who.admin === true);
        setOidcProviders(rc.oidc_providers ?? []);
      } catch {
        setCanInvite(false);
        setWebauthnOn(false);
        setIsAdmin(false);
        setOidcProviders([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inline, session.access_token, engineReady]);

  // verifyTransparency audits that this account's key is committed in the server's
  // signed transparency log — recomputing the leaf client-side from the sigchain,
  // verifying the signed root + the Merkle inclusion proof in the WASM engine.
  async function verifyTransparency() {
    if (!engineReady || !engine) {
      message.error("Crypto engine not ready");
      return;
    }
    setKtBusy(true);
    try {
      const proof = await hs.transparencyProof(session.access_token, session.user_id);
      const chain = await hs.getSigchain(session.access_token, session.user_id);
      const input = JSON.stringify({
        user_id: session.user_id,
        anchor: chain.anchor_key,
        links: chain.links ?? [],
        str: proof.str,
        server_key: proof.server_key,
        audit_path: proof.audit_path ?? [],
        index: proof.index,
      });
      const { verified, error } = await engine.auditKeyTransparency(input);
      if (verified) {
        setKtResult({ ok: true, msg: `Verified — your key is in the server's signed log (epoch ${proof.str.epoch}).` });
      } else {
        setKtResult({ ok: false, msg: `Not verified: ${error ?? "inclusion failed"}` });
      }
    } catch (e) {
      setKtResult({ ok: false, msg: `Audit failed: ${(e as Error).message}` });
    } finally {
      setKtBusy(false);
    }
  }

  // addProof signs a "proof" sigchain link with the device key (used as the
  // anchor) and appends it, binding the account to an external identity.
  async function addProof(v: { service: string; identity: string; proof_url?: string }) {
    if (!engineReady || !engine) {
      message.error("Crypto engine not ready");
      return;
    }
    const key = await loadDeviceKey(session.device_id);
    if (!key) {
      message.error("No device key on this browser");
      return;
    }
    setAdding(true);
    try {
      // Bootstrap the sigchain: the device key doubles as the anchor.
      let links: SigchainLink[] = [];
      try {
        const chain = await hs.getSigchain(session.access_token, session.user_id);
        links = chain.links ?? [];
        if (!chain.anchor_key) await hs.setAnchor(session.access_token, key.pub);
      } catch {
        await hs.setAnchor(session.access_token, key.pub);
      }

      let prevHash = "";
      if (links.length > 0) prevHash = (await engine.nextPrevHash(links[links.length - 1])).prev_hash;

      const link: SigchainLink = {
        seqno: links.length + 1,
        prev_hash: prevHash,
        device_id: "",
        armored_public_key: "",
        action: "proof",
        signed_by: key.fingerprint,
        service: v.service,
        identity: v.identity.trim(),
        proof_url: (v.proof_url ?? "").trim(),
      };
      link.signature = (await engine.signSigchainLink(link, key.priv)).signature;
      await hs.appendSigchainLink(session.access_token, link);
      message.success("Identity proof added");
      proofForm.resetFields();
      await loadProofs();
    } catch (e) {
      message.error(`Couldn't add proof: ${(e as Error).message}`);
    } finally {
      setAdding(false);
    }
  }

  async function changePassword(v: { old: string; next: string }) {
    setLoading(true);
    try {
      await hs.changePassword(session.access_token, v.old, v.next);
      message.success("Password changed");
      form.resetFields();
    } catch (e) {
      message.error(`Couldn't change password: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  const localpart = session.user_id.replace(/^@/, "").split(":")[0];

  const accountTab = (
    <div className="profile-tab">
      <NotificationsPanel token={session.access_token} userId={session.user_id} />
      <Typography.Title level={5}>Change password</Typography.Title>
      <Form form={form} layout="vertical" onFinish={changePassword} requiredMark={false}>
        <Form.Item name="old" rules={[{ required: true, message: "Enter your current password" }]}>
          <Input.Password placeholder="current password" autoComplete="current-password" />
        </Form.Item>
        <Form.Item name="next" rules={[{ required: true, min: 8, message: "At least 8 characters" }]}>
          <Input.Password placeholder="new password (min 8)" autoComplete="new-password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Update password
        </Button>
      </Form>

      <Divider />
      <Typography.Title level={5}>Offline media</Typography.Title>
      <MediaCachePanel />
    </div>
  );

  const securityTab = (
    <div className="profile-tab">
      <Typography.Title level={5}>Recovery codes</Typography.Title>
      <RecoveryCodesPanel session={session} />

      <Divider />
      <Typography.Title level={5}>Identity proofs</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Cryptographically bind this account to an external identity. Each proof is signed by your device key (the
        green badge). To complete the two-way binding, publish the proof's signature at its URL and hit "verify
        URL" — the server fetches it and confirms the external account posted your signature.
      </Typography.Paragraph>
      <div className="proof-list">
        {proofs.length === 0 && <div className="rail-empty">No identity proofs yet</div>}
        {proofs.map((p) => {
          const state = published[proofKey(p)];
          return (
            <div className="proof-item" key={proofKey(p)}>
              <Tooltip title="Signed by your account key">
                <SafetyCertificateTwoTone twoToneColor="#19c37d" />
              </Tooltip>
              <span className="proof-service">{p.service}</span>
              {p.proof_url ? (
                <a className="proof-identity" href={p.proof_url} target="_blank" rel="noreferrer">
                  {p.identity}
                </a>
              ) : (
                <span className="proof-identity">{p.identity}</span>
              )}
              {p.signature && (
                <Tooltip title="Copy the signature to publish at the URL">
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => navigator.clipboard?.writeText(p.signature ?? "")}
                  />
                </Tooltip>
              )}
              {p.proof_url &&
                (state === "ok" ? (
                  <Tag color="green">published ✓</Tag>
                ) : state === "missing" ? (
                  <Tag color="red">not found</Tag>
                ) : (
                  <Button size="small" type="link" loading={state === "checking"} onClick={() => checkPublished(p)}>
                    verify URL
                  </Button>
                ))}
            </div>
          );
        })}
      </div>
      <Form form={proofForm} layout="vertical" onFinish={addProof} requiredMark={false} style={{ marginTop: 8 }}>
        <Form.Item name="service" initialValue="website" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "website", label: "Website" },
              { value: "github", label: "GitHub" },
              { value: "domain", label: "Domain (DNS)" },
              { value: "twitter", label: "Twitter / X" },
            ]}
          />
        </Form.Item>
        <Form.Item name="identity" rules={[{ required: true, message: "Enter the external identity" }]}>
          <Input placeholder="e.g. alice.example.com or your-handle" />
        </Form.Item>
        <Form.Item name="proof_url">
          <Input placeholder="proof URL (where you'll publish it) — optional" />
        </Form.Item>
        <Button htmlType="submit" loading={adding} disabled={!engineReady}>
          Add proof
        </Button>
      </Form>

      {webauthnOn && (
        <>
          <Divider />
          <Typography.Title level={5}>Passkeys (passwordless sign-in)</Typography.Title>
          <PasskeyPanel session={session} />
        </>
      )}

      {oidcProviders.length > 0 && (
        <>
          <Divider />
          <Typography.Title level={5}>Linked sign-in providers</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Link an external account so you can “Sign in with …” next time.
          </Typography.Paragraph>
          <Space wrap>
            {oidcProviders.map((p) => (
              <Button
                key={p.id}
                onClick={async () => {
                  try {
                    const { url } = await hs.oidcLinkStart(session.access_token, p.id);
                    globalThis.location.href = url;
                  } catch (e) {
                    message.error(`Could not start linking: ${(e as Error).message}`);
                  }
                }}
              >
                Link {p.name}
              </Button>
            ))}
          </Space>
        </>
      )}

      <Divider />
      <Typography.Title level={5}>Logged-in devices</Typography.Title>
      <DevicesPanel session={session} hs={hs} />

      <Divider />
      <Typography.Title level={5}>Key backup</Typography.Title>
      <KeyBackupPanel session={session} engine={engine} engineReady={engineReady} hs={hs} />

      <Divider />
      <Typography.Title level={5}>Device key protection</Typography.Title>
      <KeyLockPanel engine={engine} engineReady={engineReady} userId={session.user_id} />

      <Divider />
      <Typography.Title level={5}>Security key / biometric (advanced)</Typography.Title>
      <WebAuthnPanel userId={session.user_id} />

      {isNativeApp() && (
        <>
          <Divider />
          <Typography.Title level={5}>Fingerprint / device PIN (this device)</Typography.Title>
          <BioLockPanel userId={session.user_id} />
        </>
      )}

      <Divider />
      <Typography.Title level={5}>Key transparency</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Verify your device key is committed in the server's signed, append-only transparency log — so the server can't swap in a
        device key or hide it without detection.
      </Typography.Paragraph>
      <Button onClick={verifyTransparency} loading={ktBusy} disabled={!engineReady} className="kt-verify">
        Verify my key in the transparency log
      </Button>
      {ktResult && (
        <Typography.Paragraph type={ktResult.ok ? "success" : "danger"} className="kt-result" style={{ marginTop: 8 }}>
          {ktResult.ok ? "✓ " : "⚠ "}
          {ktResult.msg}
        </Typography.Paragraph>
      )}
      {(() => {
        const server = session.user_id.split(":")[1] || "";
        const w = server ? witnessedEpochs(server) : { count: 0, latest: 0 };
        const alarm = server ? witnessAlarm(server) : null;
        if (alarm) {
          return (
            <Typography.Paragraph type="danger" style={{ marginTop: 8, marginBottom: 0 }}>
              ⚠ Witnessing detected a problem: {alarm.detail}.
            </Typography.Paragraph>
          );
        }
        if (w.count === 0) return null;
        return (
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
            Witnessed {w.count} signed root{w.count === 1 ? "" : "s"} (latest epoch {w.latest}) — cross-checked against co-members, no equivocation seen.
          </Typography.Paragraph>
        );
      })()}

      <Divider />
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, textAlign: "center", marginBottom: 0 }}>
        Zalmoxis {APP_VERSION}
      </Typography.Paragraph>
    </div>
  );

  const channelsTab = (
    <div className="profile-tab">
      <div className="directory-list">
        {joined.length === 0 && <div className="rail-empty">You haven't joined any channels</div>}
        {joined.map((r) => (
          <button
            type="button"
            className="directory-item directory-link"
            key={r.room_id}
            onClick={() => {
              onOpenRoom(r.room_id);
              onClose();
            }}
          >
            <span className="directory-name">
              {r.room_type === "m.space" ? <AppstoreOutlined className="space-icon" /> : <NumberOutlined className="room-hash" />}
              {friendlyRoomName(r)}
              {r.room_type === "m.space" && <Tag color="purple">space</Tag>}
            </span>
            <Tag>{r.num_joined_members}</Tag>
          </button>
        ))}
      </div>
    </div>
  );

  const body = (
    <>
      <div className="profile-identity">
        <div className="profile-avatar">{(localpart[0] || "?").toUpperCase()}</div>
        <div className="profile-identity-text">
          <Typography.Text strong copyable={{ text: session.user_id }}>
            {session.user_id}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            device {session.device_id}
          </Typography.Text>
        </div>
      </div>
      <Tabs
        tabPosition={screens.md ? "left" : "top"}
        defaultActiveKey="account"
        className="profile-tabs"
        items={[
          { key: "account", label: "Account", children: accountTab },
          { key: "security", label: "Security", children: securityTab },
          ...(canInvite ? [{ key: "invites", label: "Invites", children: <InvitePanel session={session} /> }] : []),
          ...(isAdmin ? [{ key: "admin", label: "Admin", children: <AdminConsole session={session} onOpenRoom={onOpenRoom} onDiscuss={onDiscussReport} /> }] : []),
          { key: "channels", label: `Channels (${joined.length})`, children: channelsTab },
        ]}
      />
    </>
  );

  // inline renders the body without the Modal wrapper (a mobile bottom-nav screen).
  if (inline) return <div className="mobile-tab-view profile-modal">{body}</div>;
  return (
    <Modal title="Profile" open={open} onCancel={onClose} footer={null} width={760} className="profile-modal">
      {body}
    </Modal>
  );
}
